import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { GradeProgressHeader } from './GradeProgressHeader';
import type { GradeQueueItemProps } from './GradeQueueItem';
import { GradeQueueItem } from './GradeQueueItem';
import { PaginatedList } from '@/components/ui/layout/PaginatedList';

export type QueueRowModel = Pick<
  GradeQueueItemProps,
  | 'studentName'
  | 'secondaryLine'
  | 'state'
  | 'displayState'
  | 'synced'
  | 'score'
  | 'maxScore'
  | 'bulkSelected'
  | 'onToggleBulk'
> & {
  key: string | number;
  focused: boolean;
  onSelect: () => void;
};

type GradeQueuePanelProps = {
  courseLabel?: string;
  assignmentLabel?: string;
  searchQuery: string;
  onSearchQuery: (q: string) => void;
  auditFilter: 'all' | 'errors' | 'drafts' | 'ready';
  onAuditFilter: (f: 'all' | 'errors' | 'drafts' | 'ready') => void;
  gradedCount: number;
  totalCount: number;
  ungradedCount: number;
  draftCount: number;
  readyCount: number;
  rows: QueueRowModel[];
  emptyMessage: string;
  footerSlot?: ReactNode;
  className?: string;
};

export function GradeQueuePanel({
  courseLabel,
  assignmentLabel,
  searchQuery,
  onSearchQuery,
  auditFilter,
  onAuditFilter,
  gradedCount,
  totalCount,
  ungradedCount,
  draftCount,
  readyCount,
  rows,
  emptyMessage,
  footerSlot,
  className,
}: GradeQueuePanelProps): ReactElement {
  const { t } = useTranslation();
  const filters = [
    ['all', t('audit.filters.all')],
    ['errors', t('audit.filters.errors')],
    ['drafts', t('audit.filters.drafts')],
    ['ready', t('audit.filters.ready')],
  ] as const;

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-3 border-r border-slate-200/80 bg-dg-bg-surface p-4 dark:border-slate-700/70 dark:bg-slate-900/30',
        className
      )}
    >
      {courseLabel ? (
        <div>
          <p className="text-dg-label font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t('audit.gradeStudio.class')}
          </p>
          <p className="mt-0.5 truncate text-dg-body font-semibold text-slate-900 dark:text-white">
            {courseLabel}
          </p>
        </div>
      ) : null}
      {assignmentLabel ? (
        <div>
          <p className="text-dg-label font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t('audit.gradeStudio.assignment')}
          </p>
          <p className="mt-0.5 truncate text-dg-body font-semibold text-slate-900 dark:text-white">
            {assignmentLabel}
          </p>
        </div>
      ) : null}

      <div>
        <label htmlFor="dg-grade-queue-search" className="sr-only">
          {t('audit.gradeStudio.searchLabel')}
        </label>
        <input
          id="dg-grade-queue-search"
          type="search"
          value={searchQuery}
          onChange={(e) => onSearchQuery(e.target.value)}
          placeholder={t('audit.gradeStudio.searchPlaceholder')}
          className="h-11 w-full rounded-dg-control border border-slate-200/90 bg-white px-3 text-dg-body text-slate-900 shadow-sm outline-none placeholder:text-slate-400 focus:border-dg-accent-grade/50 focus:ring-2 focus:ring-dg-accent-grade/20 dark:border-slate-600 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500"
          autoComplete="off"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map(([id, labelText]) => (
          <button
            key={id}
            type="button"
            onClick={() => onAuditFilter(id)}
            className={cn(
              'rounded-dg-control border px-3 py-2 text-dg-label font-medium uppercase tracking-wide transition-colors',
              auditFilter === id
                ? 'border-dg-accent-grade bg-dg-accent-grade text-white shadow-sm'
                : 'border-slate-200/80 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-600 dark:bg-slate-900/40 dark:text-slate-300 dark:hover:border-slate-500'
            )}
          >
            {labelText}
          </button>
        ))}
      </div>

      <GradeProgressHeader
        gradedCount={gradedCount}
        totalCount={totalCount}
        ungradedCount={ungradedCount}
        draftCount={draftCount}
        readyCount={readyCount}
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <p className="mb-2 shrink-0 text-dg-label font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {t('audit.studio.submissionsHeading')}
        </p>
        <PaginatedList
          items={rows}
          pageSize={5}
          resetKey={`${auditFilter}-${searchQuery}-${rows.length}`}
          emptyMessage={
            <p className="text-dg-body text-slate-500 dark:text-slate-400">{emptyMessage}</p>
          }
          keyExtractor={(r) => String(r.key)}
          renderItem={(r) => (
            <GradeQueueItem
              studentName={r.studentName}
              secondaryLine={r.secondaryLine}
              state={r.state}
              displayState={r.displayState}
              synced={r.synced}
              score={r.score}
              maxScore={r.maxScore}
              focused={r.focused}
              bulkSelected={r.bulkSelected}
              onToggleBulk={r.onToggleBulk}
              onSelect={r.onSelect}
            />
          )}
        />
      </div>

      {footerSlot ? (
        <div className="shrink-0 border-t border-slate-200/70 pt-3 dark:border-slate-700/60">
          {footerSlot}
        </div>
      ) : null}
    </div>
  );
}
